import {Component, inject, Input} from '@angular/core';
import {RequestService} from "projects/store/src/app/services/api/v1/request.service";
import {SalesWorkflowService} from "projects/store/src/app/services/api/v1/sales-workflow.service";
import {MacroFlowService} from "projects/store/src/app/services/api/v1/macro-flow.service";
import {CustomMessageService, UtilService} from "library";
import {HttpStatusCode} from "@angular/common/http";
import {ActivatedRoute} from "@angular/router";
import {EMPTY_TOP_UP_LOAN, TopUpLoan} from "projects/store/src/app/interfaces";

@Component({
  selector: 'app-sales-credit-number',
  templateUrl: './sales-credit-number.component.html'
})
export class SalesCreditNumberComponent {
  private readonly route = inject(ActivatedRoute);
  @Input({required: true}) creditNumber: any;
  @Input() data: any;
  visibleCreditResume: boolean = false;
  request: any;
  flowCode!: string;
  requestId: string;
  topUpLoan: TopUpLoan = { ...EMPTY_TOP_UP_LOAN };
  topUpLoanLoaded: boolean = false;

  constructor(private requestService: RequestService,
              private salesWorkflowService: SalesWorkflowService,
              private utilService: UtilService, public macroFlowService: MacroFlowService,
              private customMessageService: CustomMessageService) {
    this.request = this.requestService.request;
    this.flowCode = this.route.snapshot.params['flow_code'];
    this.requestId = this.route.snapshot.params['request_id'];
  }

  onResumeCredit() {
    this.visibleCreditResume = true;
    if (this.isTopUpLoan && !this.topUpLoanLoaded) {
      this.loadTopUpLoan();
    }
  }

  getCanCancelRequest(): boolean {
    return this.salesWorkflowService.canCancelRequest();
  }

  frequencyText(frequency?: number): string {
    const freq = frequency
      ?? this.request?.quotes?.frecuency
      ?? this.data?.quotes?.frecuency;

    return this.utilService.getFrequencyDescription(freq);
  }

  frequencyTextSingular(frequency?: number): string {
    return this.utilService.getFrequencyDescription(frequency ?? this.data?.quotes?.frecuency, false, true);
  }

  calculateFinancedAmount(): number {
    const price = this.data?.quotes?.prices?.selected || 0;
    const installment = this.data?.quotes?.installment?.selected || 0;
    return price - installment;
  }

  hasAdditionalProduct(): boolean {
    return !!this.data?.additional_product;
  }

  getAdditionalProduct(): any {
    return this.data?.additional_product;
  }

  getAdditionalProductNetAmount(): number {
    const additionalProduct = this.getAdditionalProduct();
    if (!additionalProduct) {
      return 0;
    }

    if (additionalProduct.net_amount !== undefined && additionalProduct.net_amount !== null) {
      return Number(additionalProduct.net_amount) || 0;
    }

    const amount = Number(additionalProduct.amount) || 0;
    const discountPct = Number(additionalProduct.discount_pct) || 0;
    return amount * (1 - discountPct / 100);
  }

  getProductsSubtotal(): number {
    return Number(this.data?.quotes?.prices?.selected) || 0;
  }

  getMainProductAmount(): number {
    const selectedPrice = this.getProductsSubtotal();
    if (!this.hasAdditionalProduct()) {
      return selectedPrice;
    }

    return selectedPrice - this.getAdditionalProductNetAmount();
  }

  getInstallmentAmount(): number {
    return Number(this.data?.quotes?.installment?.selected) || 0;
  }

  getFinancedTotalAmount(): number {
    return this.getProductsSubtotal() - this.getInstallmentAmount();
  }

  get isTopUpLoan(): boolean {
    return !!this.data?.top_up_loan;
  }

  get subtotalToConsolidate(): number {
    return this.topUpLoan.current_loan.balance - this.topUpLoan.consolidation.discount;
  }

  get hasAdvancePayment(): boolean {
    return this.topUpLoan.advance_payment > 0;
  }

  private loadTopUpLoan() {
    this.utilService.enablePageLoading();
    this.requestService.getTopUpLoan(this.requestId).subscribe({
      next: (resp) => {
        if (resp.status === HttpStatusCode.Ok) {
          this.topUpLoan = resp.body.data;
          this.topUpLoanLoaded = true;
        } else {
          this.customMessageService.responseError();
        }
        this.utilService.disablePageLoading();
      },
      error: (error) => {
        this.customMessageService.responseNoticeHandler(error);
        this.utilService.disablePageLoading();
      }
    });
  }
}
